import {
  BinFileActionResponse,
  DeleteFileResponse,
  FilesAndFolderPayloadType,
  FilesAndFoldersResponse,
  FoldersApiResponse,
  PreSignedURLResponse,
  ProcessedFileResponse,
  SummaryResponse,
  ContractFoldersResponse,
  MoveFilesPayload,
  MoveFilesResponse,
  CollectionFilesParams,
  CollectionFilesResponseData,
  ConsolidateCompareResponse,
  RenameFolderPayload,
  RenameFolderResponse,
  ResolveDuplicatesPayload,
  ResolveDuplicatesResponse,
} from '@consolidocs-ui/types';
import { enhanceEndpoints } from '../core';
import { isEqual } from 'lodash';

/**
 * Path node in the folder hierarchy
 */
export interface PathNode {
  id: number;
  name: string;
}

/**
 * Original path item returned by the original-paths API
 * Contains the Originals folder ID and path hierarchy
 */
export interface OriginalPathItem {
  id: number;  // The Originals folder ID to use in move API
  name: string;  // Always "Originals"
  paths: PathNode[][];  // Array of path variations (Party A -> Party B vs Party B -> Party A)
}

/**
 * Adding typing for tags for Files and Folder APIs
 */
const FilesAndFolderApiWithTag = enhanceEndpoints({
  addTagTypes: ['files', 'dashboard', 'file-feedback', 'file_id'],
});

// Cache to store the last response for comparison
let lastFilesResponse: FilesAndFoldersResponse | null = null;

// File and Folder API Services
export const FileAndFolderApi = FilesAndFolderApiWithTag.injectEndpoints({
  endpoints: (builder) => ({
    getAllFiles: builder.query<
      FilesAndFoldersResponse,
      FilesAndFolderPayloadType
    >({
      query: (params) => {
        const cleanedParams = Object.entries(params).reduce(
          (acc, [key, value]) => {
            if (
              value === undefined ||
              value === null ||
              value === '' ||
              (Array.isArray(value) && value.length === 0)
            ) {
              return acc;
            }

            acc[key] = Array.isArray(value) ? value.join(',') : value;
            return acc;
          },
          {} as Record<string, string>
        );

        return {
          url: 'files',
          params: {
            ...cleanedParams,
            page_size: 1000,
          },
        };
      },
      transformResponse: (response: FilesAndFoldersResponse) => {
        // If the response data is deeply equal to the last response, return the cached reference
        // This prevents unnecessary re-renders
        if (lastFilesResponse && isEqual(lastFilesResponse.data, response.data)) {
          return lastFilesResponse;
        }
        lastFilesResponse = response;
        return response;
      },
      providesTags: ['files'],
      // Keep previous data while fetching new data to prevent flicker
      keepUnusedDataFor: 60,
      // Use structural sharing to prevent re-renders when data hasn't changed
      serializeQueryArgs: ({ queryArgs }) => {
        // Create a stable cache key based on query parameters
        return JSON.stringify(queryArgs);
      },
    }),
    getFolders: builder.query<FoldersApiResponse, FilesAndFolderPayloadType>({
      query: (params) => {
        const cleanedParams = Object.entries(params).reduce(
          (acc, [key, value]) => {
            if (
              value === undefined ||
              value === null ||
              value === '' ||
              (Array.isArray(value) && value.length === 0)
            ) {
              return acc;
            }
    
            acc[key] = Array.isArray(value) ? value.join(',') : value;
            return acc;
          },
          {} as Record<string, string>
        );
    
        return {
          url: '/folders/',
          method: 'GET',
          params: {
            ...cleanedParams,
            page_size: 1000,
          },
        };
      },
      providesTags: ['files'],
    }),
    uploadFiles: builder.mutation<FilesAndFoldersResponse, FormData>({
      query: (formData) => ({
        url: 'upload/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['files', 'dashboard'],
    }),
    deleteFiles: builder.mutation<DeleteFileResponse, number[]>({
      query: (fileIds) => {
        return {
          url: `files/batch-delete/`,
          method: 'POST',
          data: { file_ids: fileIds },
        };
      },
      invalidatesTags: ['files', 'dashboard'],
    }),
    /**
     * Settle every duplicate group at once: one kept file id per group, the
     * other copies in that group are the backend's to discard.
     */
    resolveDuplicates: builder.mutation<
      ResolveDuplicatesResponse,
      ResolveDuplicatesPayload
    >({
      query: (data) => ({
        url: 'files/resolve-duplicates/',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['files', 'dashboard'],
    }),
    /**
     * Put one binned file back where it came from. Keyed by file id: the Bin
     * groups files by contract for reading, but acts on them one at a time.
     */
    restoreFileFromBin: builder.mutation<BinFileActionResponse, number>({
      query: (fileId) => ({
        url: `files/bin/${fileId}/restore/`,
        method: 'POST',
      }),
      invalidatesTags: ['files', 'dashboard'],
    }),
    /** Drop one binned file for good. Nothing brings it back. */
    permanentlyDeleteFileFromBin: builder.mutation<
      BinFileActionResponse,
      number
    >({
      query: (fileId) => ({
        url: `files/bin/${fileId}/permanent-delete/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['files', 'dashboard'],
    }),
    getPreSignedUrl: builder.query<PreSignedURLResponse, number>({
      query: (file_id) => ({
        url: `files/${file_id}/presigned-url`,
      }),
    }),
    getFileData: builder.query<ProcessedFileResponse, number>({
      query: (file_id) => ({
        url: `processed_data/${file_id}`,
      }),
      providesTags: (result, error, fileId) => [
        { type: 'file_id' as const, id: fileId },
      ],
    }),
    // Returns the server-rendered HTML document for a file. Fetched through the
    // authed base query so the Bearer token is attached (an iframe `src` can't),
    // then rendered via `srcDoc`.
    getProcessedHtml: builder.query<string, number>({
      query: (file_id) => ({
        url: `processed_html/${file_id}`,
      }),
    }),
    getChatSummary: builder.query<
      SummaryResponse,
      { file_id?: number; file_id_2?: number }
    >({
      query: (params) => {
        if (!params.file_id && !params.file_id_2) return undefined; // <-- skip when both file_id and file_id_2 are falsy
        return {
          url: `chat-summary`,
          params,
        };
      },
    }),
    getHighLightedData: builder.mutation<ConsolidateCompareResponse, any>({
      query: (formData) => {
        return {
          url: 'consolidate/compare',
          method: 'POST',
          data: formData,
        };
      },
    }),
    sortFile: builder.mutation<any, any>({
      query: (formData) => {
        return {
          url: 'files/sort-files',
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['files', 'dashboard'],
    }),
    consolidateFile: builder.mutation<
      {
        data: {
          data: {
            consolidation_successful: boolean;
            failed_consolidations: number;
            failures: Array<any>;
            results: Array<any>;
            successes: Array<any>;
            successful_consolidations: number;
            total_processed: number;
          };
          status_code: number;
          status_message: string;
        };
      },
      {
        file_ids: number[];
      }
    >({
      query: (data) => {
        return {
          url: `consolidate/consolidate_by_files`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['files', 'dashboard'],
    }),
    batchUpdateFiles: builder.mutation<
      any,
      {
        file_ids: number[];
        relative_path?: string;
        folder_id?: number;
      }
    >({
      query: (data) => {
        return {
          url: `files/batch-update/`,
          method: 'POST',
          data: {
            file_ids: data.file_ids,
            ...(data.relative_path
              ? { relative_path: data.relative_path }
              : {}),
            ...(data.folder_id ? { folder_id: data.folder_id } : {}),
          },
        };
      },
      invalidatesTags: ['files', 'dashboard'],
    }),
    editFile: builder.mutation<
      any,
      {
        file_id?: number;
        party_one?: string;
        party_two?: string;
        contract_title?: string;
        contract_date?: string;
        filename?: string;
        edit_all?: boolean;
        signatures?: {
          party_id: string;
          party_signed: boolean;
          signed_on?: string;
        }[];
        files?: number[];
        [key: string]: any;
      }
    >({
      query: (data) => {
        return {
          url: `files/file-edit`,
          method: 'PATCH',
          data,
        };
      },
      invalidatesTags: ['files', 'dashboard', 'file_id'],
    }),
    submitFileFeedback: builder.mutation<
      { success: boolean },
      {
        file_id: number;
        feedback: any[];
        context: any;
        feedback_type: string;
        rating?: number;
      }
    >({
      query: (data) => ({
        url: 'files/file-feedback/',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['file-feedback'],
    }),
    updateFileFeedback: builder.mutation<
      { success: boolean },
      {
        feedback_id: string;
        feedback: any[];
        context: any;
        feedback_type: string;
        rating?: number;
      }
    >({
      query: (data) => ({
        url: 'files/file-feedback/',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['file-feedback'],
    }),
    getFileFeedback: builder.query<
      {
        file_id: number;
        feedback: any[];
        context: string;
        feedback_type: string;
        rating?: number;
        created_at?: string;
        idx: string;
      }[],
      number
    >({
      query: (fileId) => ({
        url: 'files/file-feedback/',
        params: { file_id: fileId },
      }),
      transformResponse: (response: {
        status_code: number;
        status_message: string;
        data: {
          file_id: number;
          feedback: any[];
          context: string;
          feedback_type: string;
          rating?: number;
          created_at?: string;
          idx?: string;
        }[];
      }) => response.data.map((item, index) => ({
        ...item,
        idx: item.idx || `feedback-${item.file_id}-${index}`,
      })),
      providesTags: ['file-feedback'],
    }),
    getRelatedFiles: builder.query<
      {
        file_id: number;
        related_files: {
          id: number;
          filename: string;
        }[];
      },
      number
    >({
      query: (fileId) => ({
        url: `related-files/${fileId}`,
      }),
    }),
    /**
     * Get available contract folders for file move destination
     * Returns Party A - Party B - Contract Title - Contract Date combinations
     */
    getContractFolders: builder.query<ContractFoldersResponse, FilesAndFolderPayloadType>({
      query: (params) => {
        const cleanedParams = Object.entries(params).reduce(
          (acc, [key, value]) => {
            if (
              value === undefined ||
              value === null ||
              value === '' ||
              (Array.isArray(value) && value.length === 0)
            ) {
              return acc;
            }
            acc[key] = Array.isArray(value) ? value.join(',') : value;
            return acc;
          },
          {} as Record<string, string>
        );

        return {
          url: '/folders/contract-folders/',
          method: 'GET',
          params: cleanedParams,
        };
      },
      providesTags: ['files'],
    }),
    /**
     * Get original paths for file move destination
     * Returns list of Originals folders with their path hierarchy
     */
    getOriginalPaths: builder.query<
      OriginalPathItem[],
      { department_id: string }
    >({
      query: ({ department_id }) => ({
        url: `original-paths/${department_id}/`,
        method: 'GET',
        params: {},
      }),
      providesTags: ['files'],
    }),
    /**
     * Get collection files with folder structure and consolidated files
     * Returns folders (for Collections tree), consolidated_files, and consolidated_count
     */
    getCollectionFiles: builder.query<
      {
        status_code: number;
        status_message: string;
        data: CollectionFilesResponseData;
      },
      CollectionFilesParams
    >({
      query: (params) => {
        const cleanedParams = Object.entries(params).reduce(
          (acc, [key, value]) => {
            if (value === undefined || value === null || value === '') {
              return acc;
            }
            acc[key] = String(value);
            return acc;
          },
          {} as Record<string, string>
        );

        return {
          url: 'collection-files/',
          method: 'GET',
          params: cleanedParams,
        };
      },
      providesTags: ['files', 'dashboard'],
    }),
    /**
     * Move files to a different contract folder
     * - Updates Party A, Party B, Contract Title
     * - Maintains file movement history
     * - Triggers summary regeneration
     */
    moveFilesToFolder: builder.mutation<MoveFilesResponse, MoveFilesPayload>({
      query: (data) => {
        return {
          url: `files/move-to-folder/`,
          method: 'POST',
          data: {
            file_ids: data.file_ids,
            destination_folder_id: data.destination_folder_id,
            destination_path: data.destination_path,
          },
        };
      },
      invalidatesTags: ['files', 'dashboard'],
    }),
    /**
     * Move files, or whole folders, into a destination folder.
     * Unified API for every move (drag-drop and modal alike): pass `file_ids`
     * to move individual files, or `source_folder_ids` to move the folders
     * themselves. Exactly one of the two is expected.
     */
    moveMultipleFiles: builder.mutation<
      {
        status_code?: number;
        status_message?: string;
        // Response can have data nested or at top level
        data?: {
          moved_files?: number[];
          failed_files?: number[];
        };
        moved_files?: number[];
        failed_files?: number[];
      },
      {
        /** Destination folder - a single-element list */
        folder_ids: number[];
        /** Files to move - omit when moving folders */
        file_ids?: number[];
        /** The folders being moved - omit when moving individual files */
        source_folder_ids?: number[];
      }
    >({
      query: (data) => {
        return {
          url: `multiple-file-move/`,
          method: 'POST',
          data: {
            folder_ids: data.folder_ids,
            ...(data.file_ids ? { file_ids: data.file_ids } : {}),
            ...(data.source_folder_ids
              ? { source_folder_ids: data.source_folder_ids }
              : {}),
          },
        };
      },
      invalidatesTags: (result, _error, arg) => {
        const moved =
          result?.data?.moved_files ?? result?.moved_files ?? arg?.file_ids ?? [];
        const fileIds = Array.isArray(moved) ? moved : [];
        return [
          'files',
          'dashboard',
          ...fileIds.map((id: number) => ({ type: 'file_id' as const, id })),
        ];
      },
    }),
    /**
     * Rename a folder. Invalidates 'files' so the tree picks up the new name,
     * and 'dashboard' because folder names appear in its counts and lists.
     */
    renameFolder: builder.mutation<RenameFolderResponse, RenameFolderPayload>({
      query: ({ folder_id, folder_name }) => ({
        url: 'folders/rename/',
        method: 'POST',
        data: { folder_id, folder_name },
      }),
      invalidatesTags: ['files', 'dashboard'],
    }),
    getFileHistory: builder.query<
      {
        status_code: number;
        status_message: string;
        data: Array<{
          idx: string;
          file_id: number;
          old_values: Record<string, any>;
          new_values: Record<string, any>;
          first_name: string;
          last_name: string;
          updated_at: string;
          created_at: string;
        }>;
      },
      number
    >({
      query: (file_id) => ({
        url: `file-history/${file_id}`,
        method: 'GET',
      }),
      providesTags: ['file_id'],
    }),
  }),
});

export const {
  useGetAllFilesQuery,
  useDeleteFilesMutation,
  useUploadFilesMutation,
  useLazyGetPreSignedUrlQuery,
  useGetFileDataQuery,
  useLazyGetFileDataQuery,
  useGetProcessedHtmlQuery,
  useGetHighLightedDataMutation,
  useSortFileMutation,
  useConsolidateFileMutation,
  useBatchUpdateFilesMutation,
  useGetChatSummaryQuery,
  useGetFoldersQuery,
  useEditFileMutation,
  useSubmitFileFeedbackMutation,
  useUpdateFileFeedbackMutation,
  useGetFileFeedbackQuery,
  useGetRelatedFilesQuery,
  useGetContractFoldersQuery,
  useLazyGetContractFoldersQuery,
  useMoveFilesToFolderMutation,
  useGetFileHistoryQuery,
  useMoveMultipleFilesMutation,
  useGetOriginalPathsQuery,
  useGetCollectionFilesQuery,
  useRenameFolderMutation,
  useResolveDuplicatesMutation,
  useRestoreFileFromBinMutation,
  usePermanentlyDeleteFileFromBinMutation,
} = FileAndFolderApi;
